import React, { useCallback, useEffect, useMemo, useState } from 'react'
import dayjs from '../../utils/dayjsConfig'
import { fetchWithAuth, BASE_BACKEND_URL } from '../../auth'
import usePagination from '../../layout/usePagination'
import { DEFAULT_PER_PAGE } from '../../constants/constant'
import QuoteIndexView from './QuoteIndexView'
import { QUOTE_FILTER_KEYS } from '../helpers/quoteHelpers'

const buildOptions = (items, valueKey, labelBuilder) => {
  const seen = new Set()
  return items.reduce((options, item) => {
    const value = String(item[valueKey] || '').trim()
    if (!value || seen.has(value)) return options
    seen.add(value)
    options.push({ value, label: labelBuilder(item) })
    return options
  }, [])
}

const QuoteIndexApp = () => {
  const [isFetching, setIsFetching] = useState(true)
  const [data, setData] = useState([])
  const [filterData, setFilterData] = useState([])
  const [selectedFilters, setSelectedFilters] = useState(new Map())
  const [filterVersion, setFilterVersion] = useState(0)
  const [selectedDates, setSelectedDates] = useState(null)
  const [dateQuery, setDateQuery] = useState(null)
  const [searchInput, setSearchInput] = useState('')
  const [appliedSearchInput, setAppliedSearchInput] = useState('')
  const { pagination, setPagination, handlePaginationClick } = usePagination({
    per_page: DEFAULT_PER_PAGE
  })

  const { filters, labelToIdMapping } = useMemo(() => {
    const labels = new Map()
    const clients = buildOptions(
      filterData,
      'client_uid',
      (item) => `${item.client_uid} - ${item.client_name}`
    )
    const salesmen = buildOptions(
      filterData.filter((item) => item.user_uid),
      'user_uid',
      (item) => `${item.user_uid} - ${item.user_full_name}`
    )

    clients.concat(salesmen).forEach((option) => labels.set(option.label, option.value))

    return {
      filters: [
        { key: QUOTE_FILTER_KEYS.client, value: clients.map((option) => option.label) },
        { key: QUOTE_FILTER_KEYS.salesman, value: salesmen.map((option) => option.label) }
      ],
      labelToIdMapping: labels
    }
  }, [filterData])

  const buildFiltersQuery = useCallback((options) => {
    let query = ''
    options.forEach((value, key) => {
      if (!value.length) return
      if (key === QUOTE_FILTER_KEYS.client) query += `&client_uid=${value.join(',')}`
      if (key === QUOTE_FILTER_KEYS.salesman) query += `&salesman_uid=${value.join(',')}`
    })
    return query
  }, [])

  const filterQuery = useMemo(
    () => buildFiltersQuery(selectedFilters),
    [buildFiltersQuery, selectedFilters]
  )

  const fetchFilterData = useCallback(async () => {
    const response = await fetchWithAuth(
      `${BASE_BACKEND_URL}/quotes?client_names_only=true`
    )
    setFilterData(response.result || [])
  }, [])

  const fetchQuotes = useCallback(async () => {
    const page = pagination.current_page
    const perPage = pagination.per_page
    const hasFilters = filterQuery || dateQuery || appliedSearchInput
    const response = await fetchWithAuth(
      `${BASE_BACKEND_URL}/quotes?page=${page}&per_page=${perPage}${hasFilters ? '&filter_search=true' : ''}${filterQuery || ''}${dateQuery || ''}${appliedSearchInput ? `&q=${appliedSearchInput}` : ''}`
    )

    setPagination((prev) => ({
      ...prev,
      total_pages: Math.ceil((response.count || 0) / prev.per_page)
    }))
    setData(response.table || [])
    setIsFetching(false)
  }, [pagination.current_page, pagination.per_page, filterQuery, dateQuery, appliedSearchInput, setPagination])

  useEffect(() => {
    fetchFilterData()
  }, [fetchFilterData])

  useEffect(() => {
    fetchQuotes()
  }, [fetchQuotes])

  const updateAllSelectedOptions = (identifier, selectedLabels) => {
    const labels = Array.isArray(selectedLabels)
      ? selectedLabels
      : Array.from(selectedLabels || [])
    const selectedIds = labels.map((label) => labelToIdMapping.get(label)).filter(Boolean)

    setSelectedFilters((prev) => {
      const next = new Map(prev)
      next.set(identifier, selectedIds)
      return next
    })
    pagination.current_page = 1
  }

  const handleDateFilter = (dateObjects) => {
    if (!Array.isArray(dateObjects) || !dateObjects.length) {
      setSelectedDates(null)
      setDateQuery(null)
      return
    }

    const start = dayjs(dateObjects[0].toDate()).startOf('day')
    const end = dayjs((dateObjects[1] || dateObjects[0]).toDate()).endOf('day')
    setSelectedDates(dateObjects)
    setDateQuery(`&start_date=${start.toISOString()}&end_date=${end.toISOString()}`)
    pagination.current_page = 1
  }

  const resetFilters = () => {
    setSelectedFilters(new Map())
    setSelectedDates(null)
    setDateQuery(null)
    setSearchInput('')
    setAppliedSearchInput('')
    setFilterVersion((version) => version + 1)
  }

  const handleEnterSearch = (event) => {
    if (event.key !== 'Enter') return
    setAppliedSearchInput(searchInput)
    pagination.current_page = 1
  }

  return (
    <QuoteIndexView
      data={data}
      filters={filters}
      filterVersion={filterVersion}
      isFetching={isFetching}
      pagination={pagination}
      handlePaginationClick={handlePaginationClick}
      updateAllSelectedOptions={updateAllSelectedOptions}
      handleDateFilter={handleDateFilter}
      selectedDates={selectedDates}
      searchInput={searchInput}
      handleSearch={(event) => setSearchInput(event.target.value)}
      handleEnterSearch={handleEnterSearch}
      resetFilters={resetFilters}
      isAnyFilterSelected={Boolean(filterQuery || dateQuery || appliedSearchInput)}
    />
  )
}

export default QuoteIndexApp
